import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type MouseEvent,
  type CSSProperties,
} from "react";

// A tiny dependency-free router for the marketing site. The site is a small,
// finite set of static routes, so this avoids pulling react-router into a
// pnpm workspace. It supports pushState navigation, the browser back/forward
// buttons, in-page hash links, and scroll restoration.

type RouterState = {
  path: string;
  hash: string;
  /** Query string including the leading "?" (e.g. "?q=pair"), or "". */
  search: string;
  navigate: (to: string) => void;
};

const RouterContext = createContext<RouterState | null>(null);

function isModifiedClick(event: MouseEvent) {
  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

function isExternal(to: string) {
  return (
    /^(https?:)?\/\//.test(to) ||
    to.startsWith("mailto:") ||
    to.startsWith("tel:")
  );
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(() => window.location.pathname);
  const [hash, setHash] = useState(() => window.location.hash);
  const [search, setSearch] = useState(() => window.location.search);

  useEffect(() => {
    const onPop = () => {
      setPath(window.location.pathname);
      setHash(window.location.hash);
      setSearch(window.location.search);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    if (isExternal(to)) {
      window.location.href = to;
      return;
    }
    const url = new URL(to, window.location.origin);
    const samePage = url.pathname === window.location.pathname;
    window.history.pushState({}, "", url.pathname + url.search + url.hash);
    setPath(url.pathname);
    setHash(url.hash);
    setSearch(url.search);

    if (url.hash) {
      // Defer so the target route has rendered before we scroll to the anchor.
      requestAnimationFrame(() => {
        const el = document.getElementById(url.hash.slice(1));
        if (el) el.scrollIntoView({ behavior: samePage ? "smooth" : "auto" });
        else window.scrollTo(0, 0);
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const value = useMemo(
    () => ({ path, hash, search, navigate }),
    [path, hash, search, navigate],
  );

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
}

export function useRouter() {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error("useRouter must be used within RouterProvider");
  return ctx;
}

type LinkProps = {
  to: string;
  className?: string;
  children: ReactNode;
  "aria-label"?: string;
  title?: string;
  style?: CSSProperties;
};

/** Client-side navigation link. Falls back to a normal anchor for external URLs. */
export function Link({ to, className, children, ...rest }: LinkProps) {
  const { navigate } = useRouter();
  const external = isExternal(to);

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (external || isModifiedClick(event)) return;
    event.preventDefault();
    navigate(to);
  };

  return (
    <a
      href={to}
      className={className}
      onClick={onClick}
      {...(external ? { rel: "noopener" } : {})}
      {...rest}
    >
      {children}
    </a>
  );
}
